import { formatDate } from "@angular/common";
import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import {
  DocumentCategory,
  PropertyType,
  SharingDataCategory,
  SharingScope,
} from "@eemo/api-client";

@Injectable({ providedIn: "root" })
export class SharingHelper {
  constructor(@Inject(LOCALE_ID) public locale: string) {}

  getSharingDataCategories(): SharingDataCategory[] {
    // build the aray manually to control the order
    const categ: SharingDataCategory[] = [];
    categ.push(SharingDataCategory.LOCATION);
    categ.push(SharingDataCategory.BUILDING);
    categ.push(SharingDataCategory.ENERGY);
    categ.push(SharingDataCategory.FINANCIAL);
    categ.push(SharingDataCategory.CONDOMINIUM);
    categ.push(SharingDataCategory.PHOTO);
    categ.push(SharingDataCategory.TIMELINE);
    categ.push(SharingDataCategory.MAINTENANCE);
    categ.push(SharingDataCategory.CONSUMPTION);
    categ.push(SharingDataCategory.WORK);
    categ.push(SharingDataCategory.PEOPLE);
    categ.push(SharingDataCategory.SHARING);
    //categ.push(SharingDataCategory.BOOKLET);
    //categ.push(SharingDataCategory.EQUIPMENT);
    return categ;
  }

  getSharingDefaultDataCategory(): SharingDataCategory[] {
    return this.getSharingDataCategories().filter(
      (c) =>
        SharingDataCategory.SHARING !== c && SharingDataCategory.PEOPLE !== c
    );
  }

  getSharingDurations(): string[] {
    const durations: string[] = [];
    durations.push("1WEEK");
    durations.push("1MONTH");
    durations.push("6MONTH");
    durations.push("ALWAYS");
    return durations;
  }

  getSharingDurationName(duration: string): string {
    switch (duration) {
      case "1WEEK":
        return "1 semaine";
      case "1MONTH":
        return "1 mois";
      case "6MONTH":
        return "6 mois";
      case "ALWAYS":
        return "Toujours";
      default:
        return "Durée inconnu";
    }
  }
  getSharingDataCategoryName(
    category: SharingDataCategory,
    propType?: PropertyType
  ): string {
    if (
      propType &&
      propType === PropertyType.CONDOMINIUM &&
      category === DocumentCategory.PEOPLE
    ) {
      return "Syndic";
    }
    switch (category) {
      case SharingDataCategory.SHARING:
        return "Accès";
      case SharingDataCategory.LOCATION:
        return "Localisation";
      case SharingDataCategory.BUILDING:
        return "Construction";
      case SharingDataCategory.ENERGY:
        return "Énergie";
      case SharingDataCategory.FINANCIAL:
        return "Finance";
      case SharingDataCategory.PEOPLE:
        return "Confidentiel";
      case SharingDataCategory.CONDOMINIUM:
        return "Copropriété";
      case SharingDataCategory.PHOTO:
        return "Photos";
      case SharingDataCategory.TIMELINE:
        return "Événements";
      case SharingDataCategory.WORK:
        return "Travaux";
      case SharingDataCategory.EQUIPMENT:
        return "Équipements";
      case SharingDataCategory.MAINTENANCE:
        return "Entretiens";
      case SharingDataCategory.CONSUMPTION:
        return "Consommations";

      default:
        return "Catégorie inconnue";
    }
  }

  getSharingScopeName(categ: SharingScope): string {
    switch (categ) {
      case SharingScope.DELEGATION:
        return "Délégation";
      case SharingScope.VIEWING:
        return "Consultation";
      default:
        return "Type inconnu";
    }
  }

  getAllowedDataName(
    allowedData: SharingDataCategory[],
    scope: SharingScope
  ): string {
    if (SharingScope.VIEWING === scope) {
      if (allowedData.length === 0) {
        return "Par défaut";
      } else if (
        allowedData.length === this.getSharingDataCategories().length
      ) {
        return "Complètes";
      } else if (allowedData && allowedData.length > 0) {
        return "Sélection";
      }
    }
    return "Complètes";
  }

  getAllowedDataDetailNames(
    allowedData: SharingDataCategory[],
    propType?: PropertyType
  ): string {
    let text = "";
    if (allowedData && allowedData.length > 0) {
      allowedData.forEach(
        (data) =>
          (text += this.getSharingDataCategoryName(data, propType) + ", ")
      );
      return text.substring(0, text.length - 2);
    }
    return text;
  }

  calculateEndDate(duration: string, date?: string): string {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const dateArg = date ? new Date(Date.parse(date)) : undefined;

    const refDate = dateArg && dateArg > now ? dateArg : now;
    let endDate: Date;
    switch (duration) {
      case "1WEEK":
        endDate = new Date(refDate.setDate(refDate.getDate() + 7));
        break;
      case "1MONTH":
        endDate = new Date(refDate.setMonth(refDate.getMonth() + 1));
        break;
      case "6MONTH":
        endDate = new Date(refDate.setMonth(refDate.getMonth() + 6));
        break;
      default:
        return null;
    }
    return formatDate(endDate, "yyyy-MM-dd", this.locale);
  }

  getExpirationDateName(endDateStr: string): string {
    if (!endDateStr || endDateStr === "") {
      return "Jamais";
    }
    const endDate = new Date(Date.parse(endDateStr));
    const now = new Date();
    endDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diffMs = endDate.getTime() - now.getTime();
    if (diffMs > 0) {
      const diffDay = Math.floor(diffMs / 1000 / 3600 / 24);
      if (diffDay > 1) {
        if (diffDay > 7) {
          if (diffDay > 30) {
            const diffMonth = Math.floor(diffDay / 30);
            return "Dans " + diffMonth + " mois";
          } else {
            const diffWeek = Math.floor(diffDay / 7);
            return (
              "Dans " + diffWeek + " " + (diffWeek > 1 ? "semaines" : "semaine")
            );
          }
        } else {
          return "Dans " + diffDay + " jours";
        }
      } else {
        if (diffDay === 1) {
          return "Demain";
        }
        return "Aujourd'hui";
      }
    }
    return "Expiré";
  }

  isSharingExpired(endDateStr: string): boolean {
    if (!endDateStr || endDateStr === "") {
      return false;
    }
    const endDate = new Date(Date.parse(endDateStr));
    const now = new Date();

    return endDate < now;
  }
}
